/** Helpers for script-audio-align word-synced visuals. */

import {
  ARTIFACT_TYPE_IDS,
  artifactLabel,
  isTitleElement,
} from './scriptAudioAlignArtifacts';

export const VISUAL_TYPES = ARTIFACT_TYPE_IDS;

export function visualTypeLabel(type) {
  return artifactLabel(type);
}

/** Words available as trigger tokens for given sentence indices. */
export function wordsForSentenceIndices(sentences, sentenceIndices) {
  const wanted = new Set((sentenceIndices || []).map((i) => Number(i)));
  const rows = [];
  for (const s of sentences || []) {
    if (wanted.size && !wanted.has(Number(s.index))) continue;
    for (const w of s.words || []) {
      rows.push({
        word: w.word,
        token: w.token,
        global_start: w.global_start,
        global_end: w.global_end,
        sentence_index: s.index,
      });
    }
  }
  return rows;
}

export function triggerWordOptions(sentences, sentenceIndices) {
  const seen = new Set();
  const opts = [];
  for (const row of wordsForSentenceIndices(sentences, sentenceIndices)) {
    const label = row.word || row.token;
    if (!label || seen.has(label)) continue;
    seen.add(label);
    opts.push({
      value: label,
      label: `${label} (${Number(row.global_start).toFixed(2)}s)`,
    });
  }
  return opts;
}

/** Active visual element ids at playhead from screenplay events. */
export function activeVisualIdsAtPlayhead(screenplayEvents, playhead) {
  const t = Number(playhead) || 0;
  const byElement = new Map();

  for (const evt of screenplayEvents || []) {
    if (evt.action !== 'appear') continue;
    const start = Number(evt.global_time) || 0;
    const end = evt.global_end != null ? Number(evt.global_end) : start + 30;
    if (start <= t + 0.02 && t < end) {
      byElement.set(evt.element_id, evt);
    }
  }
  return Array.from(byElement.keys());
}

/** Resolve what to show in preview: active sync, or first/upcoming cue when idle. */
export function resolvePreviewVisuals(screenplayEvents, visualElements, playhead) {
  const byId = visualElementByCueId(visualElements);
  const t = Number(playhead) || 0;
  const activeIds = activeVisualIdsAtPlayhead(screenplayEvents, t);
  if (activeIds.length) {
    return {
      mode: 'active',
      elements: activeIds.map((id) => byId[id]).filter(Boolean),
      triggersAt: null,
    };
  }

  const appears = (screenplayEvents || [])
    .filter((evt) => evt.action === 'appear')
    .sort((a, b) => Number(a.global_time) - Number(b.global_time));

  const next = appears.find((evt) => Number(evt.global_time) > t + 0.02);
  const first = appears[0];
  const fallback = (t < 0.05 && first) ? first : next;

  if (fallback) {
    const el = byId[fallback.element_id];
    if (el) {
      return {
        mode: t < 0.05 ? 'preview' : 'upcoming',
        elements: [el],
        triggersAt: Number(fallback.global_time),
        triggerWord: fallback.trigger?.token || fallback.trigger?.word || '',
      };
    }
  }

  return { mode: 'empty', elements: [], triggersAt: null };
}

/** Group visual cues into topics; each title/topic cue starts a new segment. */
export function buildTopicSegments(visualPlan, visualElements) {
  const byId = visualElementByCueId(visualElements);
  const topics = [];
  let current = null;

  const isTitleCue = (cue) =>
    isTitleElement({ visual_type: cue.visual_type, payload: byId[cue.cue_id]?.payload });

  for (const cue of visualPlan || []) {
    if (isTitleCue(cue)) {
      if (current) topics.push(current);
      const el = byId[cue.cue_id];
      current = {
        titleCueId: cue.cue_id,
        title: String(cue.heading || el?.payload?.heading || 'Topic').trim() || 'Topic',
        cueIds: [cue.cue_id],
      };
    } else if (current) {
      current.cueIds.push(cue.cue_id);
    } else {
      const el = byId[cue.cue_id];
      current = {
        titleCueId: null,
        title: String(cue.heading || el?.payload?.heading || 'Lesson').trim() || 'Lesson',
        cueIds: [cue.cue_id],
      };
    }
  }
  if (current) topics.push(current);
  return topics;
}

export function findTopicForCueId(topics, cueId) {
  const id = String(cueId);
  return (topics || []).find((topic) => topic.cueIds.includes(id)) || null;
}

/**
 * Resolve preview layout: full-screen topic intro vs lesson with sticky title bar.
 */
export function resolvePreviewLayout(screenplayEvents, visualElements, visualPlan, playhead) {
  const base = resolvePreviewVisuals(screenplayEvents, visualElements, playhead);
  const topics = buildTopicSegments(visualPlan, visualElements);

  if (!base.elements.length) {
    return {
      ...base,
      layout: 'empty',
      topicTitle: '',
      topic: null,
      contentElements: [],
    };
  }

  const primary = base.elements.find((el) => !isTitleElement(el)) || base.elements[0];
  const topic = findTopicForCueId(topics, primary.cue_id);
  const topicTitle = topic?.title || primary.payload?.heading || 'Lesson';

  const showingTitleOnly = base.elements.every((el) => isTitleElement(el));
  if (showingTitleOnly) {
    return {
      ...base,
      layout: 'topic_intro',
      topicTitle,
      topic,
      contentElements: [],
    };
  }

  const contentElements = base.elements.filter((el) => !isTitleElement(el));
  return {
    ...base,
    layout: 'lesson',
    topicTitle,
    topic,
    contentElements: contentElements.length ? contentElements : base.elements,
  };
}

/** Fetch auth-protected audio and return a blob object URL for <audio src>. */
export async function fetchAuthenticatedAudioUrl(audioUrl) {
  const { mediaUrlForPlayback } = await import('@/lib/mediaUrls');
  const src = mediaUrlForPlayback(audioUrl);
  if (!src) return '';
  if (src.startsWith('blob:') || src.startsWith('data:')) return src;

  const token = typeof localStorage !== 'undefined'
    ? localStorage.getItem('storycraft_token')
    : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(src, { headers, credentials: 'include' });
  if (!res.ok) {
    throw new Error(`Audio fetch failed (${res.status})`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export function visualElementByCueId(visualElements) {
  return Object.fromEntries(
    (visualElements || []).map((el) => [String(el.cue_id), el]),
  );
}

export function emptyVisualCue(sentenceIndex = 0) {
  return {
    cue_id: `cue_${Date.now().toString(36)}`,
    visual_type: 'explanation',
    heading: '',
    content: '',
    sentence_indices: [sentenceIndex],
    trigger_word: '',
    disappear_word: null,
    suitability_reason: '',
    status: 'confirmed',
  };
}

export function parseMermaidEdges(mermaid) {
  const text = extractMermaidSource({ body: mermaid });
  const nodes = new Map();
  const edges = [];

  const nodePatterns = [
    /(\w+)\["([^"]+)"\]/g,
    /(\w+)\[([^\]"]+)\]/g,
    /(\w+)\(\(([^)]+)\)\)/g,
    /(\w+)\(([^)]+)\)/g,
    /(\w+)\{([^}]+)\}/g,
  ];

  for (const re of nodePatterns) {
    let match;
    while ((match = re.exec(text)) !== null) {
      if (!nodes.has(match[1])) nodes.set(match[1], match[2].trim());
    }
  }

  for (const line of mermaidLines(text)) {
    if (/^(graph|flowchart|sequencediagram|mindmap)\b/i.test(line)) continue;
    const edge = line.match(
      /(\w+)(?:\[[^\]]*\]|\([^)]*\)|\{[^}]*\})?\s*(?:-->|---|-.->|==>)\s*(?:\|[^|]*\|\s*)?(\w+)(?:\[[^\]]*\]|\([^)]*\)|\{[^}]*\})?/,
    );
    if (edge) edges.push({ from: edge[1], to: edge[2] });
  }

  for (const edge of edges) {
    if (!nodes.has(edge.from)) nodes.set(edge.from, edge.from);
    if (!nodes.has(edge.to)) nodes.set(edge.to, edge.to);
  }

  return { nodes: Object.fromEntries(nodes), edges };
}

/** Pull Mermaid source from common payload shapes (body fences, content, mermaid). */
export function extractMermaidSource(payload) {
  const raw = payload?.mermaid || payload?.body || payload?.content || '';
  let text = String(raw).trim();
  const fenced = text.match(/```(?:mermaid)?\s*([\s\S]*?)```/i);
  if (fenced) text = fenced[1].trim();
  return text.replace(/^```mermaid\s*/i, '').replace(/```\s*$/i, '').trim();
}

export function looksLikeMermaid(text) {
  const lower = String(text || '').toLowerCase();
  return (
    lower.includes('graph ')
    || lower.includes('flowchart ')
    || lower.includes('sequencediagram')
    || lower.includes('mindmap')
    || /-->|---|-.->|==>/.test(String(text || ''))
  );
}

function mermaidLines(text) {
  const lines = [];
  for (const line of String(text || '').split('\n')) {
    for (const part of line.split(';')) {
      const trimmed = part.trim();
      if (trimmed) lines.push(trimmed);
    }
  }
  return lines;
}

function assignMermaidLayers(nodeIds, edges) {
  if (!nodeIds.length) return [];
  const inDegree = Object.fromEntries(nodeIds.map((id) => [id, 0]));
  const adj = Object.fromEntries(nodeIds.map((id) => [id, []]));
  for (const edge of edges) {
    if (!adj[edge.from]) adj[edge.from] = [];
    if (adj[edge.from] && nodeIds.includes(edge.to)) {
      adj[edge.from].push(edge.to);
      inDegree[edge.to] = (inDegree[edge.to] || 0) + 1;
    }
  }

  const layer = Object.fromEntries(nodeIds.map((id) => [id, 0]));
  const queue = nodeIds.filter((id) => inDegree[id] === 0);
  if (!queue.length) queue.push(nodeIds[0]);

  const visited = new Set();
  while (queue.length) {
    const id = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);
    for (const to of adj[id] || []) {
      layer[to] = Math.max(layer[to] || 0, (layer[id] || 0) + 1);
      inDegree[to] -= 1;
      if (inDegree[to] <= 0) queue.push(to);
    }
  }

  const maxLayer = Math.max(0, ...Object.values(layer));
  const layers = Array.from({ length: maxLayer + 1 }, () => []);
  for (const id of nodeIds) {
    const li = layer[id] || 0;
    if (!layers[li].includes(id)) layers[li].push(id);
  }
  return layers.filter((row) => row.length);
}

/** Position nodes for a top-down Mermaid-style flowchart SVG. */
export function layoutMermaidGraph(parsed, options = {}) {
  const nodeIds = Object.keys(parsed?.nodes || {});
  if (!nodeIds.length) {
    return { nodes: [], edges: [], width: 320, height: 120, offsetX: 40, offsetY: 40, nodeW: 180, nodeH: 44 };
  }

  const nodeW = options.nodeW || 184;
  const nodeH = options.nodeH || 44;
  const gapX = options.gapX || 40;
  const gapY = options.gapY || 72;

  const layers = assignMermaidLayers(nodeIds, parsed.edges || []);
  const positions = {};

  layers.forEach((layer, layerIndex) => {
    const rowW = layer.length * nodeW + Math.max(0, layer.length - 1) * gapX;
    const startX = -rowW / 2 + nodeW / 2;
    layer.forEach((id, colIndex) => {
      positions[id] = {
        id,
        label: parsed.nodes[id] || id,
        x: startX + colIndex * (nodeW + gapX),
        y: layerIndex * (nodeH + gapY),
      };
    });
  });

  const placed = Object.values(positions);
  const xs = placed.map((p) => p.x);
  const ys = placed.map((p) => p.y);
  const minX = Math.min(...xs) - nodeW / 2;
  const maxX = Math.max(...xs) + nodeW / 2;
  const minY = Math.min(...ys) - nodeH / 2;
  const maxY = Math.max(...ys) + nodeH / 2;

  return {
    nodes: placed,
    edges: parsed.edges || [],
    width: Math.max(320, maxX - minX + 80),
    height: Math.max(160, maxY - minY + 80),
    offsetX: -minX + 40,
    offsetY: -minY + 40,
    nodeW,
    nodeH,
  };
}

export function normalizeMermaidPayload(payload) {
  const p = { ...(payload || {}) };
  const source = extractMermaidSource(p);
  if (source) p.mermaid = source;
  return p;
}

export function buildTreeFromNodes(flatNodes) {
  const list = Array.isArray(flatNodes) ? flatNodes : [];
  const byId = Object.fromEntries(list.map((n) => [String(n.id), { ...n, children: [] }]));
  const roots = [];
  for (const n of list) {
    const id = String(n.id);
    const pid = n.parent_id != null ? String(n.parent_id) : null;
    if (pid && byId[pid]) {
      byId[pid].children.push(byId[id]);
    } else {
      roots.push(byId[id]);
    }
  }
  return roots;
}
